/**
 * @file: useFastOrderSearch.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { useOrderSearchForm } from '@euler/app/flows/search/context';
import { useInfiniteListWithCursor } from '@euler/functions/useInfiniteListWithCursor';
import { IdType } from '@euler/model/common';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { isValidLicensePlateNo } from '@euler/utils/validators';
import { useCallback } from 'react';

export function useFastOrderSearch(limit = 15) {
  const { orderService } = useServiceFactory();

  const { form } = useOrderSearchForm();
  const status = form.status.value;

  const query = usePersistFn(
    async (nextKey: IdType | undefined, s: string | undefined) => {
      if (!s?.trim()) {
        return {
          data: [],
          cursor: undefined,
        };
      }
      return await orderService.searchOrders(
        {
          status,
          licensePlateNo: isValidLicensePlateNo(s)
            ? s.toUpperCase()
            : undefined,
          keyword: isValidLicensePlateNo(s) ? undefined : s,
        },
        nextKey,
        limit,
      );
    },
  );

  const { fetch, fetchMore, fetchSinceLastError, ...list } =
    useInfiniteListWithCursor({
      limit,
      query,
    });

  const onScrollToBottom = useCallback(() => {
    void fetchMore().catch(onErrorIgnore);
  }, [fetchMore]);

  return {
    onScrollToBottom,
    fetch,
    fetchMore,
    fetchSinceLastError,
    ...list,
  };
}
